import {
  Injectable,
  Injector,
  NgZone,
  ApplicationRef
} from '@angular/core';
import {
  ComponentType,
  LMContextService,
  LMMessageWrapperDialogRef,
  LMModalDialogWrapperRef,
  LMModalOpenService
} from '@app/services';

/**
 * This service is used to create a modal dialog, based on the content
 * of an Angular Component on the screen.
 */
@Injectable()
export class LMModalWrapperService {
  /**
   * Constructor.
   *
   * @param appRef - application reference; must not be null.
   * @param injector - the current in scope injector, use as a delegate.
   * @param ngZone - the angular zone; must not be null.
   * @param lmContextService - current context
   * @param lmModalOpenService - service tracking open dialogs
   * @router
   */
  constructor(
    private readonly appRef: ApplicationRef,
    private readonly injector: Injector,
    private readonly ngZone: NgZone,
    private readonly lmContextService: LMContextService,
    private lmModalOpenService: LMModalOpenService
  ) { }

  /**
   * Creates a modal dialog defined by the given component, returning a reference to the
   * dialog that can be interacted with.
   *
   * The component has the potential to include the dialog reference when it is
   * instantiated, as a provider.
   *
   * The dialog won't necessarily be open yet, see the dialog ref api for further methods.
   *
   * @param component - The type of the component to instantiate; must not be null.
   * @param sohoOptions - The soho control options to use.
   * @param idsOptions - The web components control options to use.
   *
   * @return the modal dialog reference.
   */
  public modal<T>(component: ComponentType<T>, sohoOptions?: SohoModalOptions, idsOptions?: any): LMModalDialogWrapperRef<T> {
    this.checkNotNull('component', component);

    sohoOptions = sohoOptions || {};
    // idsOptions = idsOptions || { title: '' };

    return new LMModalDialogWrapperRef<T>(
      this.appRef, this.injector, this.ngZone, this.lmModalOpenService, this.lmContextService, sohoOptions, component);
  }

  /**
   * Creates a modal dialog (using the content defined in the options), returning a reference to the
   * dialog that can be interacted with.
   *
   * The dialog won't be open.
   *
   * @return the modal dialog reference.
   */
  public modalMessage<T>(content: string | JQuery): LMModalDialogWrapperRef<T> {
    const sohoSettings: SohoModalOptions = { content };
    // const idsSettings: IdsModalSettings = { title: '', contents: content as string };

    // Without a component, there is no destroy callback to ensure
    // the dialog's markup is removed.
    return new LMModalDialogWrapperRef<T>(
      this.appRef, this.injector, this.ngZone, this.lmModalOpenService, this.lmContextService, sohoSettings);
  }

  /**
   * Creates a modal dialog (using the content defined in the options), returning a reference to the
   * dialog that can be interacted with.
   *
   * The dialog won't be open.
   *
   * @return the modal dialog reference.
   */
  public message(options?: SohoMessageOptions): LMMessageWrapperDialogRef {
    return new LMMessageWrapperDialogRef(this.lmModalOpenService, this.lmContextService).options(options);
  }

  /**
   * Creates an error message, defined by the given options.
   *
   * The dialog won't be open until open is called on the returned instance,
   * see the message ref api for further methods.
   *
   * @return the message reference.
   */
  error(options?: SohoMessageOptions): LMMessageWrapperDialogRef {
    return new LMMessageWrapperDialogRef(this.lmModalOpenService, this.lmContextService).options(options).status('error');
  }

  /**
   * Creates an alert message, defined by the given options.
   *
   * The dialog won't be open until open is called on the returned instance,
   * see the message ref api for further methods.
   *
   * @return the message reference.
   */
  alert(options?: SohoMessageOptions): LMMessageWrapperDialogRef {
    return new LMMessageWrapperDialogRef(this.lmModalOpenService, this.lmContextService).options(options).status('alert');
  }

  /**
   * Creates an confirm (positive) message, defined by the given options.
   *
   * The dialog won't be open until open is called on the returned instance,
   * see the message ref api for further methods.
   *
   * @return the message reference.
   */
  confirm(options?: SohoMessageOptions): LMMessageWrapperDialogRef {
    return new LMMessageWrapperDialogRef(this.lmModalOpenService, this.lmContextService).options(options).status('confirm');
  }

  /**
   * Creates an info message, defined by the given options.
   *
   * The dialog won't be open until open is called on the returned instance,
   * see the message ref api for further methods.
   *
   * @return the message reference.
   */
  info(options?: SohoMessageOptions): LMMessageWrapperDialogRef {
    return new LMMessageWrapperDialogRef(this.lmModalOpenService, this.lmContextService).options(options).status('info');
  }

  /**
   * Creates an success message, defined by the given options.
   *
   * The dialog won't be open until open is called on the returned instance,
   * see the message ref api for further methods.
   *
   * @return the message reference.
   */
  success(options?: SohoMessageOptions): LMMessageWrapperDialogRef {
    return new LMMessageWrapperDialogRef(this.lmModalOpenService, this.lmContextService).options(options).status('success');
  }

  /**
   * Checks the given parameter is not null, throws an exception if not.
   */
  private checkNotNull(parameterName: string, arg: any) {
    if (arg === null || arg === undefined) {
      throw new Error(`The parameter/input '${parameterName}' must not be null.`);
    }
  }
}
